import type {
  ChannelCourseResource,
  ChannelExpandedCommon,
} from '../../../../../api/channel';
import type {UserModel} from '../../../../../core/domain/User/User.model';
import type {Nullable} from '../../../../../utils/utility-types';
import type {StrategyByType} from './types';

import {membershipStore} from '../../../../../api/membership';
import {
  getCourseLabel,
  isCourseCorporate,
  isCourseEnded,
  isCourseIndividual,
} from '../../../../../utils/entity/course';
import {isMeCoachForEntity} from '../../../../../utils/entity/isMeCoachForEntity';
import {getUserSource} from '../../../../../utils/entity/user/getUserSource';
import {envManager} from '../../../../../utils/env';

export class ChannelCourseStrategy implements StrategyByType {
  getAvatar(channel: ChannelExpandedCommon) {
    const course = (channel.resource as ChannelCourseResource) || null;

    return course?.program?.avatar || null;
  }

  getClientName(channel: ChannelExpandedCommon) {
    const course = channel.resource as Nullable<ChannelCourseResource>;

    return course?.client?.deleted
      ? 'Deleted user'
      : course?.client?.name || 'Client not found';
  }

  getClientSource(channel: ChannelExpandedCommon): UserModel.UserSource {
    const course = channel.resource as Nullable<ChannelCourseResource>;

    if (!course) {
      return 'direct';
    }

    // @ts-expect-error: it's a legacy (OK!)
    return getUserSource(course.client);
  }

  getDescription(channel: ChannelExpandedCommon) {
    const course = (channel.resource as ChannelCourseResource) || null;

    return course ? getCourseLabel(course) : '';
  }

  getIsArchived(channel: ChannelExpandedCommon) {
    return channel.resource?.status === 'archived';
  }

  getIsCorporate(channel: ChannelExpandedCommon) {
    return isCourseCorporate(channel.resource as ChannelCourseResource);
  }

  getIsEnded(channel: ChannelExpandedCommon) {
    const course = channel.resource as ChannelCourseResource | null;

    return isCourseEnded(course);
  }

  getIsIndividual(channel: ChannelExpandedCommon) {
    const course = channel.resource as ChannelCourseResource | null;

    return isCourseIndividual(course);
  }

  getIsNewClient(channel: ChannelExpandedCommon) {
    const course = channel.resource as Nullable<ChannelCourseResource>;

    return Boolean(
      isMeCoachForEntity(course) &&
        channel.subtenant_id &&
        channel.coach_first_message_date === null,
    );
  }

  getIsOneToOne(channel: ChannelExpandedCommon) {
    const course = channel?.resource as ChannelCourseResource | null;
    const coachesCount = course?.coach_ids?.length || 0;
    const clientsCount = course?.counters?.memberships?.accepted || 0;
    const totalParticipants = coachesCount + clientsCount;

    return totalParticipants === 2;
  }

  getIsStarted(channel: ChannelExpandedCommon) {
    return channel?.resource?.status === 'ongoing';
  }

  getTitle(channel: ChannelExpandedCommon) {
    const course = channel.resource as Nullable<ChannelCourseResource>;

    let title = course?.program?.title || 'Program not found';

    if (isMeCoachForEntity(course) && course?.client_id) {
      const clientName = this.getClientName(channel);

      if (this.getIsCorporate(channel)) {
        title = clientName;

        const subtenant = course.subtenant;

        if (subtenant) {
          const subtenantTitle = subtenant.title;
          const notFoundTenantTitle = envManager.isCore
            ? 'Fake tenant'
            : 'Tenant not found';
          const tenantTitle = subtenant.tenant?.title || notFoundTenantTitle;

          title += ` (${subtenantTitle}/${tenantTitle})`;
        }
      } else {
        title += ` (${clientName})`;
      }

      return title;
    }

    return title;
  }

  getUserIsFrozen(channel: ChannelExpandedCommon, isUserCoach: boolean) {
    if (isUserCoach) {
      return false;
    }

    const membership = membershipStore.membershipLookup.get(
      channel.resource_id || '',
    );

    return membership ? membership.status === 'frozen' : false;
  }
}
